import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import {
  useGetFilterDomainZonesQuery,
  useGetProjectBrandDomainsQuery,
  usePostAffiliateRuleCreateCustomZoneMutation,
  usePostAffiliateRuleUpdateMutation,
} from '../../../../app/api'
import { hideModal, setModal } from '../../../../app/store/reducers/AppSlice'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { useParams } from 'react-router-dom'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { extractDomainZone } from '../../../../shared/lib/helpers/ExtractDomainZone'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import { AddDomain } from './AddDomain'
import { arraysEqual } from '../../../../shared/lib/helpers/ArrayEqual'
import { useTranslation } from 'react-i18next'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import styles from './UpdateDomains.module.scss'

interface IFormValues {
  url: string
  params: Record<string, { type: string; ignored_values?: string; allowed_values?: string }>
  domainZones: string[]
}

export const UpdateDomains = (props: any) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [disabled, setDisabled] = useState(true)
  const [changed, setChanged] = useState(false)
  const dispatch = useAppDispatch()

  const form = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      domainZones: props.domain_zones || [],
    },
  })

  const {
    register,
    watch,
    setValue,
    formState: { isValid },
  } = form

  const formValues = watch()

  const [updateDomains] = usePostAffiliateRuleUpdateMutation()
  const [createCustomZone] = usePostAffiliateRuleCreateCustomZoneMutation()

  const {
    data: projectDomains,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetProjectBrandDomainsQuery(
    { projectId: Number(id), url: `https://${props.domain}${props.domain_zones[0]}` },
    { skip: !id || !props.domain || !props.domain_zones.length },
  )
  const {
    data: domainZones,
    isLoading: domainZonesLoading,
    refetch: refetchDomainZones,
  } = useGetFilterDomainZonesQuery()

  const transformDomainZones = useMemo(() => {
    return (
      domainZones?.map((zone) => ({
        ...zone,
        allowDelete: zone.value !== props.main_domain_zone,
      })) || []
    )
  }, [domainZones, formValues.url])

  const filteredDomains =
    projectDomains?.data?.filter(
      (domain: string) => !formValues.domainZones?.includes(extractDomainZone(domain)),
    ) || []

  const handleAddDomain = useCallback(
    (domain: string) => {
      const zone = domain.slice(domain.lastIndexOf('.'))
      const newZones = Array.from(new Set([...(formValues.domainZones || []), zone]))

      setValue('domainZones', newZones)
    },
    [formValues.domainZones, setValue],
  )

  const submit = (runSync = false) => {
    updateDomains({
      body: {
        scenario: 'update_zones',
        id: props.id,
        project_id: Number(id),
        domain_zones: form.getValues().domainZones,
        run_sync: runSync,
      },
    }).then(() => {
      dispatch(hideModal())
    })
  }

  useEffect(() => {
    setDisabled(!formValues?.domainZones?.length)
  }, [formValues, isValid])

  useEffect(() => {
    setChanged(!arraysEqual(props.domain_zones, formValues.domainZones))
  }, [formValues])

  if (domainZonesLoading) {
    return <Preloader />
  }

  return (
    <div className={styles.root}>
      <div>
        <div className={styles.panel}>
          <h3 className={styles.title}>{t('pages.project.editTrackingDomains')}</h3>
          <div className={styles.body}>
            <div>
              <BpSelect
                multiple
                bottomSection={
                  <button
                    className={styles.addDomainButton}
                    type={'button'}
                    onClick={() =>
                      dispatch(
                        setModal({
                          size: 'md',
                          children: (
                            <AddDomain
                              currentDomains={formValues.domainZones}
                              domains={domainZones?.map((zone) => zone.value)}
                              onSubmit={(value) => {
                                const domain = extractDomainZone(
                                  value,
                                  domainZones?.map((zone) => zone.value),
                                )

                                if (transformDomainZones.find(({ value }) => domain === value)) {
                                  setValue('domainZones', [...formValues.domainZones, domain])
                                } else {
                                  createCustomZone({
                                    body: { project_id: Number(id), url: value },
                                  }).then((response) => {
                                    const customZone = Object.keys((response as any)?.data.data)[0]

                                    refetchDomainZones().then(() => {
                                      setValue('domainZones', [
                                        ...formValues.domainZones,
                                        customZone,
                                      ])
                                    })
                                  })
                                }
                              }}
                            />
                          ),
                        }),
                      )
                    }
                  >
                    {t('pages.project.addCustomDomain')}
                    <div className={styles.addDomainIconWrap}>
                      <IconPlus color={'#A059FF'} />
                    </div>
                  </button>
                }
                dropPosition={'auto'}
                loading={!isSuccess && (isLoading || isFetching)}
                options={transformDomainZones}
                placeholder={t('pages.project.selectDomains')}
                value={formValues.domainZones}
                {...register('domainZones', {
                  onChange: (e) => {
                    setValue(
                      'domainZones',
                      [].slice.call(e.target.selectedOptions).map((o: IOption) => o.value),
                    )
                  },
                })}
              />
              {filteredDomains.length > 0 && (
                <p className={styles.domainHint}>
                  <span className={styles.domainHintLabel}>{t('pages.project.addDomains')}: </span>
                  {filteredDomains.map((domain: string, index: number) => (
                    <Fragment key={index}>
                      <span
                        key={index}
                        className={styles.domainLink}
                        onClick={() => handleAddDomain(domain)}
                      >
                        {domain}
                      </span>
                      {index !== filteredDomains.length - 1 ? ', ' : ''}
                    </Fragment>
                  ))}
                </p>
              )}
              <p className={styles.tldNote}>{t('pages.project.tldNote')}</p>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <BpButton
          className={styles.saveButton}
          disabled={disabled || !changed}
          type='button'
          onClick={() => submit()}
        >
          {t('buttons.saveChanges')}
        </BpButton>
        <BpButton
          className={styles.updateButton}
          disabled={props.is_syncing || disabled || !changed}
          theme={'outline'}
          type='button'
          onClick={() => submit(true)}
        >
          {t('pages.project.saveUpdateRule')}
        </BpButton>
      </div>
    </div>
  )
}
